import { getApiUrl } from '../config/api';

let registrationPromise = null;
let lastSyncedUserId = null;
let interactionListenerAttached = false;
let pendingPermissionContext = null;
let skipPublicKeyFetchUntil = 0;
const PUSH_DEBUG_PREFIX = '[PUSH]';
const PUSH_PUBLIC_KEY_RETRY_DELAY_MS = 5 * 60 * 1000;

const isPushFeatureEnabled = () => {
  const rawFlag = String(process.env.REACT_APP_ENABLE_PUSH_NOTIFICATIONS || '').trim().toLowerCase();
  // Default OFF to avoid noisy 503s when backend push keys are not configured.
  // Enable explicitly with REACT_APP_ENABLE_PUSH_NOTIFICATIONS=true.
  if (!rawFlag) return false;
  return ['1', 'true', 'yes', 'on'].includes(rawFlag);
};

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
};

const detectPlatform = () => {
  const userAgent = navigator.userAgent || '';
  if (/android/i.test(userAgent)) return 'android';
  if (/iphone|ipad|ipod/i.test(userAgent)) return 'ios';
  return 'web';
};

const getCurrentUserId = () => {
  try {
    const rawUser = localStorage.getItem('user');
    if (!rawUser) return null;
    const parsed = JSON.parse(rawUser);
    return parsed?._id || parsed?.id || null;
  } catch {
    return null;
  }
};

const getServiceWorkerRegistration = async () => {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.warn(PUSH_DEBUG_PREFIX, 'Service Worker or PushManager is not supported in this browser');
    return null;
  }

  if (!registrationPromise) {
    registrationPromise = (async () => {
      const base = process.env.PUBLIC_URL || '';
      const swUrl = `${base}/sw.js`;

      const existingRegistration = await navigator.serviceWorker.getRegistration().catch(() => null);
      if (existingRegistration) {
        console.info(PUSH_DEBUG_PREFIX, 'Using existing service worker registration');
        return existingRegistration;
      }

      try {
        const createdRegistration = await navigator.serviceWorker.register(swUrl);
        console.info(PUSH_DEBUG_PREFIX, 'Service worker registered from push service:', swUrl);
        return createdRegistration;
      } catch (error) {
        console.error(PUSH_DEBUG_PREFIX, 'Service worker registration failed:', error);
        return null;
      }
    })();
  }

  return registrationPromise;
};

const syncSubscription = async ({ registration, token, userId, publicKey }) => {
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    console.info(PUSH_DEBUG_PREFIX, 'No existing push subscription found, creating new one');
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey)
    });
  } else {
    console.info(PUSH_DEBUG_PREFIX, 'Existing push subscription found');
  }

  if (!subscription) {
    console.warn(PUSH_DEBUG_PREFIX, 'Push subscription creation returned empty result');
    return { success: false, reason: 'subscription_failed' };
  }

  if (lastSyncedUserId === userId) {
    console.info(PUSH_DEBUG_PREFIX, 'Subscription already synced for user', userId);
    return { success: true, reason: 'already_synced' };
  }

  const response = await fetch(getApiUrl('/api/notifications/push/subscribe'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({
      platform: detectPlatform(),
      subscription: subscription.toJSON ? subscription.toJSON() : subscription
    })
  });

  if (!response.ok) {
    const errorResult = await response.json().catch(() => ({}));
    throw new Error(errorResult.message || 'Failed to save push subscription');
  }

  const result = await response.json().catch(() => ({}));
  lastSyncedUserId = userId;
  console.info(PUSH_DEBUG_PREFIX, 'Push subscription saved on server', result);
  return { success: true, result };
};

const detachInteractionListeners = () => {
  if (!interactionListenerAttached) return;
  interactionListenerAttached = false;
  window.removeEventListener('click', handleInteractionForPermission, true);
  window.removeEventListener('touchend', handleInteractionForPermission, true);
  window.removeEventListener('keydown', handleInteractionForPermission, true);
};

async function handleInteractionForPermission() {
  if (!pendingPermissionContext) {
    detachInteractionListeners();
    return;
  }

  const context = pendingPermissionContext;
  pendingPermissionContext = null;
  detachInteractionListeners();

  try {
    const permission = await Notification.requestPermission();
    console.info(PUSH_DEBUG_PREFIX, 'Notification permission result after interaction:', permission);
    if (permission === 'granted') {
      await syncSubscription(context);
    }
  } catch (error) {
    console.error('Push permission request failed:', error);
  }
}

const armPermissionRequestOnInteraction = (context) => {
  pendingPermissionContext = context;
  if (interactionListenerAttached) return;
  interactionListenerAttached = true;
  window.addEventListener('click', handleInteractionForPermission, true);
  window.addEventListener('touchend', handleInteractionForPermission, true);
  window.addEventListener('keydown', handleInteractionForPermission, true);
};

export const registerPushNotifications = async () => {
  if (!isPushFeatureEnabled()) {
    return { success: false, reason: 'disabled_by_env' };
  }

  const token = localStorage.getItem('token');
  const userId = getCurrentUserId();

  if (!token || !userId) {
    pendingPermissionContext = null;
    detachInteractionListeners();
    return { success: false, reason: 'not_authenticated' };
  }

  if (!('Notification' in window)) {
    console.warn(PUSH_DEBUG_PREFIX, 'Notification API is not available in this browser');
    return { success: false, reason: 'notification_unsupported' };
  }

  if (Notification.permission === 'denied') {
    console.warn(PUSH_DEBUG_PREFIX, 'Notification permission is denied');
    return { success: false, reason: 'permission_denied' };
  }

  const registration = await getServiceWorkerRegistration();
  if (!registration) {
    console.warn(PUSH_DEBUG_PREFIX, 'Service worker registration is unavailable');
    return { success: false, reason: 'unsupported' };
  }

  if (skipPublicKeyFetchUntil > Date.now()) {
    return { success: false, reason: 'public_key_retry_backoff' };
  }

  try {
    let keyResponse;
    try {
      keyResponse = await fetch(getApiUrl('/api/notifications/push/public-key'));
    } catch (networkError) {
      // Server unreachable / CORS / offline — silently skip push setup.
      console.info(PUSH_DEBUG_PREFIX, 'Push notification endpoint unreachable, skipping setup');
      skipPublicKeyFetchUntil = Date.now() + PUSH_PUBLIC_KEY_RETRY_DELAY_MS;
      return { success: false, reason: 'public_key_unavailable' };
    }

    if (!keyResponse.ok) {
      // 4xx/5xx (other than not-configured) — log info, not warn.
      console.info(PUSH_DEBUG_PREFIX, 'Push not available on this server (status', keyResponse.status, ')');
      skipPublicKeyFetchUntil = Date.now() + PUSH_PUBLIC_KEY_RETRY_DELAY_MS;
      return { success: false, reason: 'public_key_unavailable' };
    }

    const keyResult = await keyResponse.json();
    const publicKey = keyResult?.publicKey;
    if (!publicKey || keyResult?.configured === false) {
      console.info(PUSH_DEBUG_PREFIX, 'Push notifications are not configured on this server, skipping');
      skipPublicKeyFetchUntil = Date.now() + PUSH_PUBLIC_KEY_RETRY_DELAY_MS;
      return { success: false, reason: 'not_configured' };
    }

    skipPublicKeyFetchUntil = 0;
    const context = { registration, token, userId, publicKey };
    const permission = Notification.permission;
    console.info(PUSH_DEBUG_PREFIX, 'Current notification permission:', permission);

    if (permission === 'default') {
      console.info(PUSH_DEBUG_PREFIX, 'Waiting for user interaction to request notification permission');
      armPermissionRequestOnInteraction(context);
      return { success: false, reason: 'awaiting_user_interaction' };
    }

    if (permission !== 'granted') {
      console.warn(PUSH_DEBUG_PREFIX, 'Notification permission is not granted');
      return { success: false, reason: 'permission_not_granted' };
    }

    return await syncSubscription(context);
  } catch (error) {
    console.error(PUSH_DEBUG_PREFIX, 'Push registration failed:', error);
    return { success: false, reason: error.message };
  }
};

if (typeof window !== 'undefined') {
  window.__debugPushRegistration = registerPushNotifications;
}
